import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowCircleUp, faArrowCircleDown } from '@fortawesome/free-solid-svg-icons'

/**
 * SortableHeader component renders a table header that is clickable for sorting.
 *
 * This component is reusable with any data table. By passing the `field` and a
 * sorting handler (`onSort`), it allows toggling between ascending and descending
 * order for that specific column. The parent component should manage the sorted data
 * and pass the current `sortBy` and `sortOrder` values back to this component.
 *
 * @param {Object} props
 * @param {string} props.field - The field name corresponding to the table column.
 * @param {string} props.label - The display label for the header.
 * @param {string} props.sortBy - The current field being sorted.
 * @param {"asc" | "desc"} props.sortOrder - The current sort order ("asc" for ascending, "desc" for descending).
 * @param {(sort: { field: string, order: "asc" | "desc" }) => void} props.onSort - Callback to trigger sorting logic in the parent.
 *
 * @example
 * <SortableHeader
 *   field="name"
 *   label="Name"
 *   sortBy={currentSortField}
 *   sortOrder={currentSortOrder}
 *   onSort={({ field, order }) => handleSort(field, order)}
 * />
 */
const SortableHeader = ({ field, label, sortBy, sortOrder, onSort }) => {
  const handleClick = () => {
    let newOrder = 'desc'
    if (sortBy === field) {
      newOrder = sortOrder === 'asc' ? 'desc' : 'asc'
    }
    onSort({ field, order: newOrder })
  }

  return (
    <th style={{ cursor: 'pointer' }} onClick={handleClick}>
      {label}
      {sortBy === field && (
        <FontAwesomeIcon
          className='ms-2'
          icon={sortOrder === 'asc' ? faArrowCircleUp : faArrowCircleDown}
        />
      )}
    </th>
  )
}

export default SortableHeader
